import {
  HEATMAP_EVENT_GROUPS,
  HEATMAP_LIMITS,
  HEATMAP_MODES,
  checkReportRange,
  daysInRange,
  typesOfGroups,
  type HeatmapMode,
  type HeatmapResult,
} from "@fts/core";
import { Suspense, lazy, useMemo, useRef, useState } from "react";
import { PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { descendantIds } from "../../lib/fleet";
import { heatmapCellsSheet } from "../../lib/report-sheets";
import { navigate, type Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { ExportBar, FilterBar, Loading, ReportMeta, filterSummary, filtersQuery, parseFilters, rangeKey, useLoad, type ExportSpec } from "./shared";

const HeatmapMap = lazy(() => import("./HeatmapMap"));

/** PDF: the densest cells listed under the map (the full list goes to CSV / XLSX). */
const PDF_TOP_CELLS = 100;

/**
 * Isı haritası (ADR 0024): where safety events happened ("Olaylar") or where vehicles were moving
 * ("Kullanım") over at most 31 days, as server-aggregated ~4 m cells on the map. No operator filter and no
 * identity in the cells (KVKK); exports are logged like every report.
 */
export function HeatmapReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const m = route.query.get("m");
  const mode: HeatmapMode = (HEATMAP_MODES as readonly string[]).includes(m ?? "") ? (m as HeatmapMode) : "events";
  const groups = f.groups.filter((g) => HEATMAP_EVENT_GROUPS.includes(g));
  // No chip = every located group (never the checklist alarms, which have no location).
  const types = mode === "events" ? typesOfGroups(groups.length ? groups : HEATMAP_EVENT_GROUPS) : [];
  const issue = checkReportRange(f.from, f.to, HEATMAP_LIMITS.maxDays);
  const tenantId = tenant?.id;
  const [mapError, setMapError] = useState(false);
  const snapshot = useRef<(() => string | null) | null>(null);

  // Vehicles of the scope (site subtree, optional selection): the usage mode's size limit, checked up front.
  const inSite = f.siteId ? descendantIds(data.sites, f.siteId) : null;
  const scopeVehicles = data.vehicles.filter((v) => (!inSite || inSite.has(v.siteId)) && (f.vehicleIds.length === 0 || f.vehicleIds.includes(v.id)));
  const tooLarge = mode === "usage" && !issue && scopeVehicles.length * daysInRange(f.from, f.to) > HEATMAP_LIMITS.maxVehicleDays;

  const query = (id: string) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, from: f.from, to: f.to, mode, types });
  const state = useLoad<HeatmapResult>(
    tenantId && !issue && !tooLarge ? () => provider.reportHeatmap(query(tenantId)) : null,
    [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.from, f.to, mode, types.join(",")],
  );
  const res = state.data;

  // Without data the map looks at the selected site (or the first site with a position, walking up the tree).
  const fallbackCenter = useMemo<[number, number] | null>(() => {
    const byId = new Map(data.sites.map((s) => [s.id, s]));
    let cur = f.siteId ? byId.get(f.siteId) : undefined;
    while (cur) {
      if (cur.lat !== null && cur.lon !== null) return [cur.lat, cur.lon];
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    const any = data.sites.find((s) => s.lat !== null && s.lon !== null);
    return any ? [any.lat!, any.lon!] : null;
  }, [data.sites, f.siteId]);

  const modeLabel = t(`reports.heatmap.mode.${mode}`);
  const keep = { m: mode === "events" ? undefined : mode };
  const setMode = (x: HeatmapMode) => navigate("/reports/heatmap", { ...filtersQuery(f), m: x === "events" ? undefined : x });
  const num = (n: number) => n.toLocaleString(locale);

  const summary = (r: HeatmapResult): [string, string][] => [
    [mode === "events" ? t("reports.heatmap.events") : t("reports.heatmap.points"), num(r.points)],
    ...(mode === "events" ? ([[t("reports.heatmap.weight"), num(r.weight)]] as [string, string][]) : []),
    [t("reports.heatmap.vehicles"), num(r.vehicles)],
    [t("reports.heatmap.cells", { size: r.cellM }), num(r.totalCells)],
    ...(mode === "events" && r.unlocated > 0 ? ([[t("reports.heatmap.unlocated"), num(r.unlocated)]] as [string, string][]) : []),
  ];

  const spec: ExportSpec | null = state.status === "ready" && res && tenantId
    ? {
        report: "heatmap",
        reportName: `${t("reports.heatmap.title")} — ${modeLabel}`,
        slug: t("reports.heatmap.slug"),
        part: `${mode}_${f.from}_${f.to}`,
        tz: res.tz,
        filters: [
          [t("reports.heatmap.mode"), modeLabel],
          ...filterSummary(t, data, { ...f, groups: mode === "events" ? groups : [] }, { operator: false, groups: true }),
          ...summary(res),
        ],
        rows: res.cells.length,
        // Cells fetched (and logged) again; the PDF shows the map, the summary and the densest cells.
        load: async (format) => {
          const r = await provider.exportHeatmap(query(tenantId), format);
          return { sheets: [heatmapCellsSheet(t, r.cells, mode, format === "pdf" ? PDF_TOP_CELLS : undefined)], tz: r.tz };
        },
        mapImage: () => (snapshot.current ? snapshot.current() : null),
        mapUnavailable: t("reports.heatmap.mapUnavailable"),
      }
    : null;

  const rk = issue === "too_long" ? "reports.heatmap.rangeTooLong" : rangeKey(issue);
  const empty = state.status === "ready" && res && res.cells.length === 0;

  return (
    <>
      <PageHeader title={t("reports.heatmap.title")} subtitle={t("reports.heatmap.subtitle")} eyebrow={t("nav.group.reports")} />
      <div className="heatmap-mode">
        <span className="field-label" id="hm-mode-label">
          {t("reports.heatmap.mode")}
        </span>
        <div className="segmented" role="group" aria-labelledby="hm-mode-label">
          {HEATMAP_MODES.map((x) => (
            <button key={x} type="button" className={x === mode ? "seg-on" : ""} aria-pressed={x === mode} onClick={() => setMode(x)}>
              {t(`reports.heatmap.mode.${x}`)}
            </button>
          ))}
        </div>
      </div>
      <FilterBar data={data} path="/reports/heatmap" filters={f} operator={false} groups={mode === "events"} groupList={HEATMAP_EVENT_GROUPS} keep={keep} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      {tooLarge && (
        <p className="form-error" role="alert">
          {t("reports.heatmap.tooLarge", { max: num(HEATMAP_LIMITS.maxVehicleDays) })}
        </p>
      )}
      <ReportMeta tz={res?.tz ?? null} sample={provider.mode === "demo"} />
      <ExportBar spec={spec} disabled={!res || res.cells.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.status === "idle" || (state.status === "loading" && !res) ? (
        rk || tooLarge ? null : <Loading />
      ) : res && state.status !== "error" ? (
        <>
          <div className="report-totals" aria-busy={state.status === "loading"}>
            {summary(res).map(([k, v]) => (
              <div key={k}>
                <span>{k}</span>
                <strong>{v}</strong>
              </div>
            ))}
          </div>
          {res.truncated && <p className="form-error">{t("reports.heatmap.truncated", { max: num(res.cells.length), total: num(res.totalCells) })}</p>}
          <Panel title={t("reports.heatmap.map")} flush>
            {empty && <p className="table-empty">{mode === "events" ? t("reports.heatmap.noEvents") : t("reports.heatmap.noUsage")}</p>}
            {mapError ? (
              <p className="table-empty">{t("reports.heatmap.mapError")}</p>
            ) : (
              <Suspense fallback={<Loading />}>
                <HeatmapMap
                  cells={res.cells}
                  maxWeight={res.maxWeight}
                  bounds={res.bounds}
                  fallbackCenter={fallbackCenter}
                  label={t("reports.heatmap.mapLabel", { mode: modeLabel })}
                  onError={() => setMapError(true)}
                  onSnapshot={(take) => (snapshot.current = take)}
                />
              </Suspense>
            )}
            <div className="map-legend heat-legend">
              <span>{t("reports.heatmap.legend")}</span>
              <span className="muted">{t("reports.heatmap.legendLow")}</span>
              <span className="heat-legend-bar" role="img" aria-label={t("reports.heatmap.legendAria")} />
              <span className="muted">{t("reports.heatmap.legendHigh")}</span>
              <span>{t("reports.heatmap.zoomHint")}</span>
            </div>
          </Panel>
        </>
      ) : null}
      <p className="panel-footnote">
        {mode === "events" ? t("reports.heatmap.rules.events", { size: HEATMAP_LIMITS.cellM }) : t("reports.heatmap.rules.usage", { size: HEATMAP_LIMITS.cellM })}{" "}
        {t("reports.heatmap.kvkk")}
      </p>
    </>
  );
}
